import { Box, Flex, List, ListItem, useOutsideClick } from '@chakra-ui/react';
import {
  FieldHelpText,
  IconButton,
  Input,
  InputProps,
  InputVariant,
  KeyboardCodes,
  SvgSpriteConsumer,
  Text,
} from '@pluxee-design-system/core';
import { SkippableBaseControlProps } from '@pluxee-design-system/react-hook-form';
import { useAddressSuggestion } from 'api/locationApi';
import { getVariant } from 'common/forms/utils';
import { RuianData } from 'generated';
import { debounce } from 'lodash';
import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useController } from 'react-hook-form';
import reactStringReplace from 'react-string-replace';

interface AddressAutocompleteControlProps
  extends Omit<
      InputProps,
      'isDisabled' | 'format' | 'name' | 'onChange' | 'value' | 'rightElement'
    >,
    SkippableBaseControlProps {
  onClear: () => void;
  onOptionSelect: (option: RuianData) => void;
  notFoundText: string;
  loadingText: string;
}

const highlightSearchedWords = (
  data: RuianData,
  { inputValue }: { inputValue: string },
): ReactNode => {
  const label =
    inputValue !== ''
      ? reactStringReplace(data.all, new RegExp(`(${inputValue})`, 'gi'), (match, i) => (
          <Text key={i} as="span" variant="body.mediumXbold">
            {match}
          </Text>
        ))
      : data.all;
  return (
    <Text variant="body.mediumDemiBold" as="span">
      {label}
    </Text>
  );
};

const AddressAutocompleteControl = ({
  control,
  disabled,
  isDisabled,
  isReadOnly,
  name,
  notFoundText,
  loadingText,
  rules,
  readonly,
  skipSuccess = false,
  skipVariant = false,
  onClear,
  onOptionSelect,
  ...inputProps
}: AddressAutocompleteControlProps) => {
  const { field, fieldState, formState } = useController({
    name,
    control,
    rules,
  });
  const { error } = fieldState;
  const variant = getVariant(
    formState,
    fieldState,
    isReadOnly ?? readonly,
    skipSuccess,
    skipVariant,
  );

  const [inputValue, setInputValue] = useState(() => field.value);
  const [areOptionsDisplayed, setAreOptionsDisplayed] = useState<boolean>(false);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: containerRef as RefObject<HTMLElement>,
    handler: () => {
      setAreOptionsDisplayed(false);
      setFocusedOptionIndex(null);
    },
  });

  const isRequestEnabled = inputValue.length > 2;
  const { data, isLoading } = useAddressSuggestion(inputValue, isRequestEnabled);
  const options = data?.entries;

  const setOptionsDisplayWithValue = useCallback((value: string) => {
    if (value) setAreOptionsDisplayed(true);
    else setAreOptionsDisplayed(false);
  }, []);
  const debouncedSetInputValue = useMemo(
    () => debounce((value: string) => setInputValue(value), 1000),
    [setInputValue],
  );

  // Handlers
  const handleBlur = useCallback(
    (_e: FocusEvent<HTMLInputElement>) => {
      setAreOptionsDisplayed(false);
      setFocusedOptionIndex(null);
      field.onBlur();
    },
    [field],
  );
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      field.onChange(e);
      setOptionsDisplayWithValue(e.currentTarget.value);
      debouncedSetInputValue(e.currentTarget.value);
    },
    [debouncedSetInputValue, field, setOptionsDisplayWithValue],
  );
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (e.key === KeyboardCodes.ENTER) {
        e.preventDefault();
        if (options && focusedOptionIndex !== null) {
          const option = options[focusedOptionIndex];
          setAreOptionsDisplayed(false);
          onOptionSelect?.(option);
          setFocusedOptionIndex(null);
        }
      }

      if (areOptionsDisplayed && options?.length && e.key === KeyboardCodes.ARROW_DOWN) {
        setFocusedOptionIndex((previousFocusedOptionIndex) => {
          if (previousFocusedOptionIndex === null) {
            return 0;
          }
          if (options[previousFocusedOptionIndex + 1]) {
            return previousFocusedOptionIndex + 1;
          }
          return 0;
        });
      }
      if (areOptionsDisplayed && options?.length && e.key === KeyboardCodes.ARROW_UP) {
        setFocusedOptionIndex((previousFocusedOptionIndex) => {
          if (previousFocusedOptionIndex === null) {
            return null;
          }
          if (options[previousFocusedOptionIndex - 1]) {
            return previousFocusedOptionIndex - 1;
          }
          return options.length - 1;
        });
      }
    },
    [areOptionsDisplayed, focusedOptionIndex, onOptionSelect, options],
  );
  const handleClickOrFocus = useCallback(
    (e: MouseEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>) => {
      setOptionsDisplayWithValue(e.currentTarget.value);
    },
    [setOptionsDisplayWithValue],
  );

  return (
    <Box width="100%" position="relative" ref={containerRef}>
      <Input
        name={field.name}
        rightElement={
          field.value ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="1.5rem"
              height="1.5rem"
            >
              <IconButton
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={onClear}
                aria-label="clear input"
                variant="icon"
                icon={
                  <SvgSpriteConsumer color="semantic.icon.primary" svgId="delete24" size={24} />
                }
              />
            </Box>
          ) : null
        }
        onBlur={handleBlur}
        onClick={handleClickOrFocus}
        onFocus={handleClickOrFocus}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        value={field.value}
        {...inputProps}
        disabled={variant !== InputVariant.READONLY && (disabled ?? isDisabled)}
        variant={variant === InputVariant.SUCCESS ? undefined : variant}
      />
      {areOptionsDisplayed && (
        <List variant="autoSuggest">
          {isLoading && isRequestEnabled ? (
            <ListItem my={1}>
              <Flex align="center">{loadingText}</Flex>
            </ListItem>
          ) : (
            <>
              {options?.map((option, optionIndex) => (
                <ListItem
                  key={option.all}
                  onMouseDown={(e) => {
                    // important to not trigger input blur when clicking on an option
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAreOptionsDisplayed(false);
                    onOptionSelect?.(option);
                    setFocusedOptionIndex(null);
                  }}
                  {...(optionIndex === focusedOptionIndex
                    ? { className: 'ds-autosuggest-focused-option' }
                    : {})}
                >
                  <Flex align="center">
                    {highlightSearchedWords(option, { inputValue: field.value ?? '' })}
                  </Flex>
                </ListItem>
              ))}
              {!options?.length && (
                <ListItem my={1}>
                  <Flex align="center">{notFoundText}</Flex>
                </ListItem>
              )}
            </>
          )}
        </List>
      )}
      {error?.message && <FieldHelpText text={error.message} variant={InputVariant.ERROR} />}
    </Box>
  );
};

export default AddressAutocompleteControl;
